import type { SweepLocation } from '../types/sweep';

export function sweepTeamKeyForReroute(item: SweepLocation): string {
  const t =
    typeof item.teamNumber === 'number' && Number.isFinite(item.teamNumber) ? Math.round(item.teamNumber) : null;
  return t == null ? '__none__' : `t${t}`;
}

export function sortItemsByStopSequence(items: SweepLocation[]): SweepLocation[] {
  return [...items].sort((a, b) => {
    const sa = typeof a.stopSequence === 'number' && Number.isFinite(a.stopSequence) ? a.stopSequence : null;
    const sb = typeof b.stopSequence === 'number' && Number.isFinite(b.stopSequence) ? b.stopSequence : null;
    if (sa == null && sb == null) return 0;
    if (sa == null) return 1;
    if (sb == null) return -1;
    return sa - sb;
  });
}

export function groupMarkersByTeam(markers: { item: SweepLocation }[]): Map<string, SweepLocation[]> {
  const groups = new Map<string, SweepLocation[]>();
  for (const m of markers) {
    const key = sweepTeamKeyForReroute(m.item);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(m.item);
  }
  return groups;
}

export type OsrmTripOptions = {
  /** When true (default), first coordinate stays first (`source=first`). */
  fixFirst?: boolean;
  /** When true, last coordinate stays last (`destination=last`). */
  fixLast?: boolean;
};

/**
 * OSRM public demo trip service — optimizes visit order (optional fixed first/last).
 * @see https://project-osrm.org/ — fair use; may rate-limit.
 */
export async function osrmTripOrderIds(
  sortedByStop: { id: string; latitude: number; longitude: number }[],
  opts?: OsrmTripOptions
): Promise<string[] | null> {
  if (sortedByStop.length < 2) return null;
  const fixFirst = opts?.fixFirst !== false;
  const fixLast = !!opts?.fixLast;
  const coordStr = sortedByStop.map((i) => `${i.longitude},${i.latitude}`).join(';');
  const params = new URLSearchParams({
    overview: 'false',
    roundtrip: 'false',
  });
  if (fixFirst) params.set('source', 'first');
  if (fixLast) params.set('destination', 'last');
  const url = `https://router.project-osrm.org/trip/v1/driving/${coordStr}?${params.toString()}`;
  try {
    const res = await fetch(url, {
      headers: {
        Accept: 'application/json',
        'User-Agent': 'SkimBusterWeb/1.0 (sweep map reroute; +https://skimbuster.com)',
      },
    });
    if (!res.ok) return null;
    const j = (await res.json()) as { code?: string; waypoints?: { waypoint_index?: number }[] };
    if (j.code !== 'Ok' || !Array.isArray(j.waypoints)) return null;
    if (j.waypoints.length !== sortedByStop.length) return null;
    /** Trip `waypoints` are in input order; `waypoint_index` is visit rank along the optimized trip (OSRM docs). */
    const ranked = j.waypoints
      .map((w, inputIdx) => ({
        inputIdx,
        visitRank: typeof w.waypoint_index === 'number' && Number.isFinite(w.waypoint_index) ? w.waypoint_index : -1,
      }))
      .filter((x) => x.visitRank >= 0);
    if (ranked.length !== sortedByStop.length) return null;
    ranked.sort((a, b) => a.visitRank - b.visitRank);
    return ranked.map((x) => sortedByStop[x.inputIdx].id);
  } catch {
    return null;
  }
}

export type NearestNeighborOptions = {
  /** When true and length >= 3, last waypoint stays last (middle only is greedy). */
  fixLast?: boolean;
};

/** Greedy nearest neighbor after the first point — offline fallback. */
export function nearestNeighborTripOrderIds(
  sortedByStop: { id: string; latitude: number; longitude: number }[],
  opts?: NearestNeighborOptions
): string[] {
  if (sortedByStop.length <= 1) return sortedByStop.map((x) => x.id);
  const fixLast = !!opts?.fixLast && sortedByStop.length >= 3;
  const end = fixLast ? sortedByStop[sortedByStop.length - 1] : null;
  const middleSlice = fixLast ? sortedByStop.slice(1, -1) : sortedByStop.slice(1);
  const rem = new Map(middleSlice.map((p) => [p.id, p]));
  const out: string[] = [sortedByStop[0].id];
  let cur = sortedByStop[0];
  while (rem.size > 0) {
    let best: string | null = null;
    let bestD = Infinity;
    for (const [id, p] of Array.from(rem.entries())) {
      const d = (cur.latitude - p.latitude) ** 2 + (cur.longitude - p.longitude) ** 2;
      if (d < bestD) {
        bestD = d;
        best = id;
      }
    }
    if (!best) break;
    const next = rem.get(best)!;
    rem.delete(best);
    out.push(best);
    cur = next;
  }
  if (fixLast && end) out.push(end.id);
  return out;
}

/**
 * Reorder `ordered` so `startId` is first and `endId` is last when present.
 * Ignores ids not in the list. If start and end are the same and length > 1, only start is fixed.
 */
export function orderWithFixedEndpoints<T extends { id: string }>(
  ordered: T[],
  startId: string | null | undefined,
  endId: string | undefined | null
): T[] {
  if (ordered.length < 2) return ordered;
  const ids = ordered.map((x) => String(x.id));
  const si = startId ? ids.indexOf(String(startId)) : -1;
  let arr = [...ordered];
  if (si > 0) {
    arr = [...arr.slice(si), ...arr.slice(0, si)];
  }
  if (!endId || String(endId) === String(startId)) return arr;
  const ei = arr.findIndex((x) => String(x.id) === String(endId));
  if (ei < 0) return arr;
  if (ei === arr.length - 1) return arr;
  const endItem = arr[ei];
  const without = arr.filter((_, i) => i !== ei);
  return [...without, endItem];
}

export function formatRerouteTeamLine(teamLabel: string, orderedIds: string[], idToItem: Map<string, SweepLocation>): string {
  const parts = orderedIds.map((id) => {
    const it = idToItem.get(id);
    if (!it) return '?';
    if (typeof it.stopSequence === 'number' && Number.isFinite(it.stopSequence)) return `#${it.stopSequence}`;
    return String(it.name || '').trim() || id.slice(0, 8);
  });
  return `${teamLabel}: ${parts.join(' → ')}`;
}

/**
 * Split one visit-ordered path into polylines: an edge is "solid" only when both endpoints are checked off.
 * Consecutive edges sharing the same style are merged (junction vertex repeated when style changes).
 */
export function splitRoutePolylinesByCheckedEdges<T extends { sweepCheckedOff?: boolean }>(
  ordered: T[],
  toPos: (t: T) => [number, number] | null
): { solid: boolean; positions: [number, number][] }[] {
  const items: { t: T; pos: [number, number] }[] = [];
  for (const t of ordered) {
    const pos = toPos(t);
    if (pos) items.push({ t, pos });
  }
  if (items.length < 2) return [];
  const out: { solid: boolean; positions: [number, number][] }[] = [];
  const runPos: [number, number][] = [items[0].pos];
  let curSolid = !!(items[0].t.sweepCheckedOff && items[1].t.sweepCheckedOff);
  for (let i = 1; i < items.length; i++) {
    const edgeSolid = !!(items[i - 1].t.sweepCheckedOff && items[i].t.sweepCheckedOff);
    if (i === 1) {
      runPos.push(items[i].pos);
      continue;
    }
    if (edgeSolid !== curSolid) {
      if (runPos.length >= 2) out.push({ solid: curSolid, positions: [...runPos] });
      curSolid = edgeSolid;
      runPos.length = 0;
      runPos.push(items[i - 1].pos, items[i].pos);
    } else {
      runPos.push(items[i].pos);
    }
  }
  if (runPos.length >= 2) out.push({ solid: curSolid, positions: [...runPos] });
  return out;
}
